import * as React from 'react'
import { useRef, useState } from 'react'
import Company from 'entities/company'
import Group from 'entities/group'
import Offer from 'entities/offer'
import Recruiter from 'entities/recruiter'
import { FiMoreVertical } from 'react-icons/fi'
import styles from './cooperation_actions_button.module.scss'
import DirectIntroductionForm from './direct_introduction_form'
import InternalScoutForm from './internal_scout_form'
import { useOutsideClick } from 'hooks/use_outside_click'
import * as classnames from 'classnames/bind'

type PrimitiveProps = {
  selectable: boolean
  deviceName: string
  contentId: number
  statuses: Array<Array<string | number>>
  direct_introduction_enabled: boolean
  internal_scout_enabled: boolean
}

type OriginalProps = {
  company: Object
  offer: Object
  group: Object
  searchGroup: Group
  introducer: Object | null
} & PrimitiveProps

type ConvertedProps = {
  company: Company
  offer: Offer
  group: Group
  searchGroup: Group
  introducer: Recruiter | null
} & PrimitiveProps

const convertPropsToJsProps = (props: OriginalProps): ConvertedProps => {
  return {
    ...props,
    introducer: props.introducer ? new Recruiter(props.introducer) : null,
    company: new Company(props.company),
    offer: new Offer(props.offer),
    group: new Group(props.group),
    searchGroup: new Group(props.searchGroup)
  }
}

type ButtonsProps = {
  internal_scout_enabled: boolean
  direct_introduction_enabled: boolean
  onOutsideClick: () => void
  onClickInternalScoutButton: () => void
  onClickDirectIntroductionButton: () => void
}

const cx = classnames.bind(styles)
const Buttons: React.FC<ButtonsProps> = ({
  internal_scout_enabled,
  direct_introduction_enabled,
  onOutsideClick,
  onClickInternalScoutButton,
  onClickDirectIntroductionButton
}) => {
  const actionsDivEl = useRef(null)
  useOutsideClick(actionsDivEl, onOutsideClick)

  const buttonClassName = cx(styles.button, styles.cooperationActionsButton)

  return (
    <div className={styles.cooperationActionsButtonsWrapper} ref={actionsDivEl}>
      {internal_scout_enabled && (
        <button type="button" className={buttonClassName} onClick={onClickInternalScoutButton}>
          {I18n.t('offers.pages.show.components.cooperation_actions_button.internal_scout_button')}
        </button>
      )}
      {direct_introduction_enabled && (
        <button type="button" className={buttonClassName} onClick={onClickDirectIntroductionButton}>
          {I18n.t('offers.pages.show.components.cooperation_actions_button.direct_introduction_button')}
        </button>
      )}
    </div>
  )
}

export const CooperationActionsButton: React.FC<OriginalProps> = props => {
  const [showActions, setShowActions] = useState(false)
  const [showInternalScout, setShowInternalScout] = useState(false)
  const [showDirectIntroduction, setShowDirectIntroduction] = useState(false)
  const jsProps = convertPropsToJsProps(props)

  return (
    <div className={styles.cooperationActionsButtonsContainer}>
      <button
        type="button"
        className={cx(styles.button, styles.showCooperationActionsButton)}
        onClick={() => setShowActions(true)}
      >
        <FiMoreVertical size={20} />
      </button>
      {showActions && (
        <Buttons
          internal_scout_enabled={jsProps.internal_scout_enabled}
          direct_introduction_enabled={jsProps.direct_introduction_enabled}
          onOutsideClick={() => setShowActions(false)}
          onClickInternalScoutButton={() => setShowInternalScout(true)}
          onClickDirectIntroductionButton={() => setShowDirectIntroduction(true)}
        />
      )}
      <InternalScoutForm
        {...jsProps}
        offer={props.offer}
        showModal={showInternalScout}
        onClose={() => setShowInternalScout(false)}
      />
      <DirectIntroductionForm
        {...jsProps}
        offer={props.offer}
        showModal={showDirectIntroduction}
        onClose={() => setShowDirectIntroduction(false)}
      />
    </div>
  )
}
